import { FC, useContext, useState, useCallback } from "react";

import { FormType, PeerToPeerQueryParams, RouteBuilders } from "@simplyk/common";

import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";

import { DonationFormV2CheckoutFooter } from "./CheckoutFooter/DonationFormV2CheckoutFooter";
import { getIsDonateMode } from "./PeerToPeer/helpers/isDonateMode";

import { ThemedContainerLoader } from "@/components/Loaders/ThemedContainerLoader";
import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useDebounce } from "@/components/TextField/useDebounce";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { removeCampaignNavigationSearchParams } from "@/features/FormV2/helpers/campaignUrls";
import { useShouldHideFundraise } from "@/features/FormV2/hooks/useShouldHideFundraise";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { ThemedDonor } from "@/features/FormV2/sharedComponents/ThemedDonor";
import { ThemedDonorsListBlock } from "@/features/FormV2/sharedComponents/ThemedDonorsListBlock";
import { ThemedDonorsListDialog } from "@/features/FormV2/sharedComponents/ThemedDonorsListDialog";
import { ThemedFundraisersInformationDialog } from "@/features/FormV2/sharedComponents/ThemedFundraisersInformationDialog";
import { ThemedFundraisersListBlock } from "@/features/FormV2/sharedComponents/ThemedFundraisersListBlock";
import { ThemedIndividualFundraiser } from "@/features/FormV2/sharedComponents/ThemedIndividualFundraiser";
import { ThemedTeamFundraiser } from "@/features/FormV2/sharedComponents/ThemedTeamFundraiser";
import {
  useThemedDonorListDialogServerState,
  useThemedDonorListDialogState,
} from "@/features/FormV2/sharedComponents/useThemedDonorListDialogState";
import { useThemedDonorsListBlockState } from "@/features/FormV2/sharedComponents/useThemedDonorsListBlockState";
import { Amounts } from "@/features/FormV2/Step1/DonationForm/Amounts";
import { useDonationFirstStepSubmit } from "@/features/FormV2/Step1/DonationForm/useDonationFirststepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { FundraiseDialog } from "@/features/FormV2/Step1/PeerToPeer/FundraiseDialog/FundraiseDialog";
import { PeerToPeerPrimaryCampaignActions } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerPrimaryCampaignActions";
import { PeerToPeerPrimaryCampaignFormV2Summary } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerPrimaryCampaignFormV2Summary";
import { useOpenPeerToPeerRegistrationForm } from "@/features/FormV2/Step1/PeerToPeer/useOpenPeerToPeerRegistrationForm";
import { PeerToPeerFormV2DonateToPrimaryCampaignStep1Layout } from "@/features/FormV2/Step1/PeerToPeerFormV2DonateToPrimaryCampaignStep1Layout";
import { PeerToPeerFormV2PrimaryCampaignStep1Layout } from "@/features/FormV2/Step1/PeerToPeerFormV2PrimaryCampaignStep1Layout";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { trpc } from "@/helpers/trpc";
import { useQueryState, parseAsBoolean, parseAsStringLiteral } from "@/hooks/useQueryState";
import { useUrlBuilder } from "@/hooks/useUrlBuilder";

export type PeerToPeerFormV2PrimaryCampaignStep1Props = {
  isEmbed?: boolean;
};

export const PeerToPeerFormV2PrimaryCampaignStep1: FC<PeerToPeerFormV2PrimaryCampaignStep1Props> = ({
  isEmbed = false,
}) => {
  const { isoLocale } = useLocaleContext();
  const { formObject, hasBanner, formCurrency, formCategory } = useFormV2Context(FormType.DonationForm);
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { isPreview, currentStepKey } = usePreviewContext();
  const { canSubmitFirstStep } = useDonationFirstStepSubmit();
  const utm = useUtmForm();
  const urlBuilder = useUrlBuilder();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.DonationForm,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const { data: getFundraisersSummaryResponse, isLoading } = trpc.getCampaignFundraisersSummary.useQuery({
    campaignId: formObject.campaignId!,
  });

  const [donateQuery] = useQueryState("donate", parseAsBoolean.withDefault(false).withOptions({ history: "push" }));
  const [fundraisersQuery, setFundraisersQuery] = useQueryState(
    "fundraisers",
    parseAsStringLiteral(["view-all", "donate-to-campaign"] as const).withOptions({ history: "push" })
  );
  const [fundraiseQuery, setFundraiseQuery] = useQueryState(
    PeerToPeerQueryParams.OpenFundraiser,
    parseAsBoolean.withDefault(false).withOptions({ history: "push" })
  );

  const fundraisers = getFundraisersSummaryResponse ?? {
    hasTeamCampaigns: false,
    hasIndividualCampaigns: false,
    items: [],
    hasMore: false,
  };
  const shouldDisplayFundraisers = fundraisers.items.length > 0;

  const isDonateMode = getIsDonateMode({
    allowTeamCreation: Boolean(formObject.allowTeamCreation),
    allowFundraiserCreation: Boolean(formObject.allowFundraiserCreation),
    allowJoinTeam: Boolean(formObject.allowJoinTeam),
    hasFundraisers: fundraisers.items.length > 0,
    isPreview,
    liveFormEditorStep: currentStepKey,
    donateQuery,
  });

  const isFundraisersModalOpen = fundraisersQuery === "view-all" || fundraisersQuery === "donate-to-campaign";
  const isFundraiseDialogOpen = fundraiseQuery;

  const [searchText, setSearchTextState] = useState("");

  const { setDebouncedValue: setSearchText } = useDebounce({
    onChange: useCallback((value: string) => {
      setSearchTextState(value);
    }, []),
    debounced: 300,
  });

  const { hasDonations, hasMoreDonations, latestDonations } = useThemedDonorsListBlockState({
    donationFormId: formObject.id,
  });

  const {
    open: donorsListDialogOpen,
    onOpen: onDonorsListDialogOpen,
    onClose: onDonorsListDialogClose,
  } = useThemedDonorListDialogState();

  const donorsDialogServerState = useThemedDonorListDialogServerState({
    donationFormId: formObject.id,
    open: donorsListDialogOpen,
  });

  const { isFetching: isSearchingFundraisers, data: searchFundraisersSummaryResponse } =
    trpc.getCampaignFundraisersSummary.useQuery(
      {
        campaignId: formObject.campaignId!,
        search: searchText,
      },
      { enabled: !!searchText && !isFundraisersModalOpen }
    );

  const searchedFundraisers = searchFundraisersSummaryResponse ?? {
    items: [],
    hasMore: false,
  };

  const shouldHideFundraise = useShouldHideFundraise();

  const openPeerToPeerRegistrationForm = useOpenPeerToPeerRegistrationForm(formObject.ticketingId ?? undefined);

  const renderFundraisersTile = () => (
    <ThemedFundraisersListBlock
      isLoading={isSearchingFundraisers}
      searchText={searchText}
      onSearchTextChange={setSearchText}
      onViewAllClick={() => setFundraisersQuery("view-all")}
      showViewAllButton={fundraisers.hasMore}
    >
      {(searchText ? searchedFundraisers.items : fundraisers.items).map(({ type, path, ...fundraiser }, index) => {
        if (type === "team") {
          return (
            <ThemedTeamFundraiser
              key={index}
              {...fundraiser}
              currency={formCurrency}
              href={urlBuilder({
                route: RouteBuilders.buildPeerToPeerV2TeamCampaignLink({ isoLocale, path }),
                searchParams: removeCampaignNavigationSearchParams,
              })}
            />
          );
        }

        return (
          <ThemedIndividualFundraiser
            key={index}
            {...fundraiser}
            currency={formCurrency}
            href={urlBuilder({
              route: RouteBuilders.buildPeerToPeerV2IndividualCampaignLink({ isoLocale, path }),
              searchParams: removeCampaignNavigationSearchParams,
            })}
            isPartOfTeam={fundraiser.isPartOfTeam}
          />
        );
      })}
    </ThemedFundraisersListBlock>
  );

  if (isLoading) {
    return <ThemedContainerLoader fullPage />;
  }

  return (
    <HeartsAnimationProvider>
      {isDonateMode ? (
        <PeerToPeerFormV2DonateToPrimaryCampaignStep1Layout
          isEmbed={isEmbed}
          contextDrawerHeight={isExpanded ? bannerHeight : 0}
          slots={{
            banner: hasBanner ? <MobileFormV2Banner /> : undefined,
            organizationLogo: (
              <FormV2LogoChip organizationName={formObject.organization?.name} logoUrl={formObject.logoUrl} />
            ),
            summary: <PeerToPeerPrimaryCampaignFormV2Summary forceInlined={isEmbed} />,
            donationAmounts: <Amounts />,
            generateReceipt: <FormV2GenerateReceiptInfobox />,
            zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
            checkoutFooter: <DonationFormV2CheckoutFooter />,
            donors: hasDonations && (
              <ThemedDonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
                {latestDonations.map((donation, index) => (
                  <ThemedDonor {...donation} key={index} />
                ))}
              </ThemedDonorsListBlock>
            ),
          }}
          showCheckoutFooter={canSubmitFirstStep && !isPreview}
          logoHref={logoHref}
          onLogoClick={onTrackLogoClick}
        />
      ) : (
        <PeerToPeerFormV2PrimaryCampaignStep1Layout
          isEmbed={isEmbed}
          contextDrawerHeight={isExpanded ? bannerHeight : 0}
          slots={{
            banner: hasBanner ? <MobileFormV2Banner /> : undefined,
            organizationLogo: (
              <FormV2LogoChip organizationName={formObject.organization?.name} logoUrl={formObject.logoUrl} />
            ),
            summary: <PeerToPeerPrimaryCampaignFormV2Summary forceInlined={isEmbed} />,
            donors: hasDonations && (
              <ThemedDonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
                {latestDonations.map((donation, index) => (
                  <ThemedDonor {...donation} key={index} />
                ))}
              </ThemedDonorsListBlock>
            ),
            fundraisers: shouldDisplayFundraisers ? renderFundraisersTile() : undefined,
            donateOrFundraiseOptions: (
              <PeerToPeerPrimaryCampaignActions
                allowTeamCreation={Boolean(formObject.allowTeamCreation)}
                allowJoinTeam={Boolean(formObject.allowJoinTeam)}
                allowFundraiserCreation={Boolean(formObject.allowFundraiserCreation)}
                hasRegistration={!!formObject.ticketingId}
                hasTeamCampaigns={fundraisers.hasTeamCampaigns}
                hasIndividualCampaigns={fundraisers.hasIndividualCampaigns}
                onDonateButtonClick={() => setFundraisersQuery("donate-to-campaign")}
                onFundraiseButtonClick={() => setFundraiseQuery(true)}
                onRegisterButtonClick={openPeerToPeerRegistrationForm}
                shouldHideFundraise={shouldHideFundraise}
              />
            ),
          }}
          logoHref={logoHref}
          onLogoClick={onTrackLogoClick}
        />
      )}
      {hasDonations && (
        <ThemedDonorsListDialog
          {...donorsDialogServerState}
          open={donorsListDialogOpen}
          onClose={onDonorsListDialogClose}
        />
      )}
      <ThemedFundraisersInformationDialog
        mode={fundraisersQuery === "view-all" ? "find-fundraiser-campaign" : "donate-to-campaign"}
        campaignId={formObject.campaignId!}
        open={isFundraisersModalOpen}
        search={searchText}
        onSearchChange={setSearchText}
        formCurrency={formCurrency}
        onClose={() => setFundraisersQuery(null)}
      />
      <FundraiseDialog
        open={isFundraiseDialogOpen}
        onClose={() => setFundraiseQuery(false)}
        campaignId={formObject.campaignId as string}
        allowTeamCreation={Boolean(formObject.allowTeamCreation)}
        allowFundraiserCreation={Boolean(formObject.allowFundraiserCreation)}
        allowJoinTeam={Boolean(formObject.allowJoinTeam)}
      />
    </HeartsAnimationProvider>
  );
};
